//
import type { ChangeEvent } from 'react';
import React from 'react';

import TextField, { type TextFieldProps } from '@mui/material/TextField/TextField';
import {
  type Control,
  type FieldValues,
  type Path,
  type RegisterOptions,
  useController,
  useFormContext,
} from 'react-hook-form';

type Props<C extends FieldValues> = {
  control: Control<C>;
  helperText?: string;
  name: Path<C>;
  rules?: Omit<RegisterOptions<C, Path<C>>, 'disabled' | 'valueAsNumber' | 'valueAsDate' | 'setValueAs'> | undefined;
} & TextFieldProps;

function FormTextField<C extends FieldValues = any>({ control, helperText, name, rules, ...other }: Props<C>) {
  const {
    field: { onBlur, onChange, value },
    fieldState: { error },
  } = useController({ control, name, rules, defaultValue: '' as any });
  const formContext = useFormContext();

  const handleChange = (event?: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (error) {
      formContext?.clearErrors();
    }
    onChange(event);
  };

  return (
    <TextField
      error={!!error}
      helperText={error?.message ? error.message : helperText}
      {...other}
      onBlur={onBlur}
      onChange={handleChange}
      value={value || ''}
    />
  );
}

export default FormTextField;
